

































































































import { Vue, Component, Prop } from 'vue-property-decorator'
import { Route } from 'vue-router'
import { ExStore } from 'vuex'
import dayjs from 'dayjs'
import store from '@/store'
import { formatDispayYearMonth } from '@/utils/datetime'
import { PdSettingPropTypes } from '@/store/pdSetting/types'
import { PdQuantityPropertyNames, PdQualityPropertyNames } from '@/types/pd'
import { PdState } from '@/store/pd/types'
import { isPlatformAndroid } from '@/utils/cordova'

const TODAY = dayjs()

const handleChangeRounting = async (to: Route, next: () => void) => {
  const patientId = Number(to.params.patientId)
  const year = Number(to.query.year || TODAY.year())
  const month = Number(to.query.month || TODAY.month() + 1)
  await Promise.all([
    store.dispatch('pd/fetch', { patientId, year, month }),
    store.dispatch('pdSetting/fetchOne', { patientId })
  ])
  next()
}

enum DisplayMode {
  ALL,
  DAILY
}

@Component({
  components: {
    PdTabQualityItem: () => import('@/components/pdTab/PdTabQualityItem.vue'),
    PdTabQuantityItem: () => import('@/components/pdTab/PdTabQuantityItem.vue'),
    PdTabSettingModalForm: () =>
      import('@/components/pdTab/settingModalForm/PdTabSettingModalForm.vue'),
    PdTabModalImage: () => import('@/components/pdTab/PdTabModalImage.vue'),
    ImageModal: () => import('@/components/imageTab/ImageModal.vue')
  }
})
export default class PdTabIndex extends Vue {
  @Prop(Number) patientId!: number
  @Prop({ default: null, type: Number }) year!: number | null
  @Prop({ default: null, type: Number }) month!: number | null

  readonly displayModeOptions = [
    { text: '全て', value: DisplayMode.ALL },
    { text: '1日毎', value: DisplayMode.DAILY }
  ]
  readonly pdSettingTypes: Array<PdSettingPropTypes> = ['emg', 'warn']
  readonly pdQualityPropertyNames: Array<PdQualityPropertyNames> = [
    'wasteFluid',
    'exitsite',
    'swelling'
  ]
  readonly qualityTitles = {
    wasteFluid: '排液チェック',
    exitsite: '出口部チェック',
    swelling: 'むくみ'
  }
  readonly pdQuantityPropertyNames: Array<PdQuantityPropertyNames> = [
    'drainVolume',
    'infusionVolume',
    'waterRemoval',
    'weight',
    'urineVolume',
    'waterDrinkingVolume'
  ]
  readonly quantityTitles = {
    drainVolume: '排液量',
    infusionVolume: '注液量',
    waterRemoval: '除水量',
    weight: '透析後体重',
    urineVolume: '尿量',
    waterDrinkingVolume: '飲水量'
  }

  $store!: ExStore
  inputDisplayMode = DisplayMode.ALL
  inputYearMonth = [TODAY.year(), TODAY.month() + 1].join('-')
  showImageModal = false
  selectedImagePdId: number | null = null
  selectedImageType: PdQualityPropertyNames | null = null
  showSettingModal = false

  get userInfo() {
    return this.$store.getters['user/userInfo']
  }

  get yearMonthOptions() {
    const startYear = 2018
    const endYear = TODAY.year()
    const endMonth = TODAY.month() + 1

    return [...new Array(endYear - startYear + 1)].reduce(
      (options, _, yearIndex) => {
        const year = endYear - yearIndex
        if (year === endYear) {
          return options.concat(
            [...new Array(endMonth)].map((_, i) => [year, endMonth - i])
          )
        }
        return options.concat([...new Array(12)].map((_, i) => [year, 12 - i]))
      },
      [] as number[]
    )
  }

  get pdAll() {
    return this.$store.getters['pd/pdAll']
  }

  get pdDaily() {
    return this.$store.getters['pd/pdDaily']
  }

  get pdGroups() {
    return this.inputDisplayMode === DisplayMode.ALL ? this.pdAll : this.pdDaily
  }

  get datetimeSort() {
    return Object.keys(this.pdGroups)
      .slice()
      .sort()
      .reverse()
  }

  formatDispayYearMonth(month: number, year: number) {
    return formatDispayYearMonth(month, year)
  }

  getDatetimeText(pd: PdState) {
    const [date, time] = (this.inputDisplayMode === DisplayMode.ALL
      ? pd.entryDayjs.format('YYYY/MM/DD HH:mm')
      : pd.entryDayjs.format('YYYY/MM/DD 00:00')
    ).split(' ')

    return `${date}<br />${time}`
  }

  handlePdCreate() {
    if (isPlatformAndroid()) {
      this.$postMessage.showPdCameraView(
        this.patientId,
        this.userInfo.isPatient
      )
      return
    }
    this.$router.push({ name: 'PdTabCreate' })
  }

  handleSelectYearMonth(event: Event) {
    const value = (event.target as HTMLInputElement).value

    if (!value) {
      return
    }

    const [year, month] = value.split('-')

    this.$router.push({ name: 'PdTab', query: { year, month } })
  }

  async handleShowImage(pdId: number, type: PdQualityPropertyNames) {
    this.showImageModal = true
    this.selectedImagePdId = pdId
    this.selectedImageType = type
  }

  handleCloseImageModal() {
    this.showImageModal = false
    this.selectedImagePdId = null
    this.selectedImageType = null
  }

  handleClickSettingThreshold() {
    this.showSettingModal = true
  }

  handleSettingModalClose() {
    this.showSettingModal = false
  }

  async beforeRouteEnter(to: Route, from: Route, next: () => void) {
    await handleChangeRounting(to, next)
  }

  async beforeRouteUpdate(to: Route, from: Route, next: () => void) {
    await handleChangeRounting(to, next)
  }
}
